import { Icon, IconProps } from '@brokerbay/elements-web';
import { Dropdown } from 'components/Dropdown';
import { Menu, MenuItem } from 'components/Menu';
import React from 'react';
import { MenuProps } from 'antd';
import styled from 'styled-components';
import { Button } from 'components/Button';
import { buildCssProp, featureEnabled } from 'utils';
import { Tooltip } from 'components/Tooltip';
import { IListing } from '@bb/domain/dist/types';

import { TOOLTIP_OPTIONS } from './constants';

type Options = {
  icon: IconProps['icon'];
  label: string;
  key: string;
  // Props type in shouldShow can be updated to include more options
  shouldShow: (props: DropdownQuickListingActionsType['isFavorite']) => boolean;
  tooltip?: string;
}[];

const getDropdownItems = (): Options => [
  {
    label: featureEnabled('propertyNotificationsRevampFeature', 1)
      ? 'Property Notifications'
      : 'Turn On Notifications',
    key: 'notifications',
    icon: 'bellOutlined',
    shouldShow: () => true,
    tooltip:
      'Book a showing on the property to receive updates including registered offers, property announcements, and feedback reminders.',
  },
  {
    label: 'Add to Favorites',
    key: 'favorites',
    icon: 'heartOutlined',
    shouldShow: isFavorite => !isFavorite,
  },
  {
    label: 'Remove from Favorites',
    key: 'nonFavorites',
    icon: 'minusOutlined',
    shouldShow: isFavorite => isFavorite,
  },
  {
    label: 'Share Listing',
    key: 'share',
    icon: 'sendOutlined',
    shouldShow: () => true,
  },
];

type DropdownQuickListingActionsType = {
  isDisabledBtns: boolean;
  isFavorite: boolean;
  isCoopAgent: boolean;
  listing: IListing & { id: string };
  toggleFavorite: (listingId: string) => Promise<void>;
};

export const DropdownQuickListingActions = ({
  isDisabledBtns,
  isFavorite,
  isCoopAgent,
  listing,
  toggleFavorite,
}: DropdownQuickListingActionsType) => (
    <Tooltip title={isDisabledBtns && TOOLTIP_OPTIONS.showing}>
      <span>
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => (
            <OptionsMenu
              options={getDropdownItems()}
              isFavorite={isFavorite}
              isCoopAgent={isCoopAgent}
              listing={listing}
              toggleFavorite={toggleFavorite}
            />
          )}
          disabled={isDisabledBtns}
        >
          <StyledDropdownButton
            data-cy="dropdown-booking-actions"
            disabled={isDisabledBtns}
            $disabled={isDisabledBtns}
          >
            <Icon icon="moreOutlined" />
          </StyledDropdownButton>
        </Dropdown>
      </span>
    </Tooltip>
  );

const OptionsMenu: React.FC<{
  options: Options;
  isFavorite: boolean;
  isCoopAgent: boolean;
  listing: DropdownQuickListingActionsType['listing'];
  toggleFavorite: DropdownQuickListingActionsType['toggleFavorite'];
}> = ({ options, isFavorite, isCoopAgent, listing, toggleFavorite }) => {
  const handleMenuClick: MenuProps['onClick'] = event => {
    const { key } = event;
    if (key === 'notifications') {
      window.location.href = `#/listing/property_update/${listing.id}`;
    } else if (key === 'nonFavorites' || key === 'favorites') {
      toggleFavorite(listing.id);
    } else if (key === 'share') {
      window.location.href = `#/listing/send/${listing.id}`;
    }
  };

  return (
    <Menu selectable={false}>
      {options.map(
        ({ icon, label, key, shouldShow, tooltip }) =>
          shouldShow(isFavorite) && (
            <MenuItem
              key={key}
              onClick={handleMenuClick}
              icon={<Icon icon={icon} size={16} />}
              disabled={
                !featureEnabled('propertyNotificationsRevampFeature', 1) &&
                !isCoopAgent &&
                key.includes('notification')
              }
            >
              <Tooltip
                title={
                  !featureEnabled('propertyNotificationsRevampFeature', 1) &&
                  !isCoopAgent &&
                  tooltip
                }
              >
                {label}
              </Tooltip>
            </MenuItem>
          ),
      )}
    </Menu>
  );
};

const StyledDropdownButton = styled(Button)<{ $disabled: boolean }>`
  && {
    display: flex;
    align-items: center;
    padding-right: 8px;
    padding-left: 8px;
    border-radius: 7px;
    ${({ $disabled }) => buildCssProp('color', !$disabled && '#FFFFFF')}
  }
`;
